import type { BookingType } from '@/types';

export interface BookingSegmentRange {
  startDate: Date;
  endDate: Date;
}

/**
 * The bar for a booking whose type auto-adjusts is drawn as one piece per run of
 * working days, so the days the booking skips (weekends, zero-hour working-profile
 * days, bank holidays) read as gaps instead of booked time.
 */
export function autoAdjustsAroundNonWorkingDays(bookingType: BookingType | undefined | null): boolean {
  return Boolean(
    bookingType?.obeyUserWorkingProfileAndBankHolidays
    && bookingType.autoAdjustBookingForWorkingProfileAndBankHolidays,
  );
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Splits [start, end] into the runs of days the resource can be booked on. A day
 * the booking skips ends the current run; the day's own times are kept, so an
 * hourly booking still starts and ends at the hours it was saved with.
 *
 * Returns a single range covering the whole booking when no day is skipped, so an
 * unaffected booking keeps rendering as exactly one bar.
 */
export function splitIntoWorkingSegments(
  start: Date,
  end: Date,
  isSkippedDay: (day: Date) => boolean,
): BookingSegmentRange[] {
  if (!(start instanceof Date) || !(end instanceof Date)) return [{ startDate: start, endDate: end }];
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return [{ startDate: start, endDate: end }];
  }

  // An end exactly at midnight closes the previous day rather than occupying this one.
  const lastCovered = startOfDay(new Date(end.getTime() - 1));
  const segments: BookingSegmentRange[] = [];
  let runStart: Date | null = null;

  for (const day = startOfDay(start); day <= lastCovered; day.setDate(day.getDate() + 1)) {
    if (isSkippedDay(day)) {
      if (runStart) {
        segments.push({ startDate: runStart, endDate: startOfDay(day) });
        runStart = null;
      }
      continue;
    }

    runStart ??= segments.length === 0 && startOfDay(start).getTime() === day.getTime()
      // The first run keeps the booking's own start time.
      ? new Date(start)
      : startOfDay(day);
  }

  if (runStart) segments.push({ startDate: runStart, endDate: new Date(end) });

  // Every day skipped: nothing sensible to draw, so leave the booking as it is.
  return segments.length === 0 ? [{ startDate: start, endDate: end }] : segments;
}
